import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import api from "../services/api";
import { jwtDecode } from "jwt-decode";
import i18n from "../i18n";
import {
  getAuthHeader,
  getToken as getStoredToken,
  setToken,
  clearToken,
} from "../services/authToken";
import { supabase } from "../lib/supabase";
import {
  persistState,
  getPersistedState,
  clearPersistedState,
  shouldInitialize,
} from "../hooks/useMobilePageLifecycle";
import {
  detectLanguage,
  storeLanguagePreference,
} from "../services/languageDetectionService";

const AuthContext = createContext();

const computeLogoutRedirect = () => {
  const publicUrl = process.env.PUBLIC_URL;

  if (!publicUrl) {
    return "/";
  }

  try {
    const resolvedUrl = new URL(publicUrl, window.location.origin);
    const pathname = resolvedUrl.pathname || "/";

    if (!pathname || pathname === "") {
      return "/";
    }

    return pathname;
  } catch (error) {
    console.warn(
      "Invalid PUBLIC_URL, falling back to root for logout redirect:",
      error
    );

    if (publicUrl.startsWith("/")) {
      return publicUrl;
    }

    return `/${publicUrl}`;
  }
};

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // GAP-MOBILE-002: Track initialization to prevent duplicate calls on mobile restore
  const hasInitializedRef = useRef(false);
  const initCountRef = useRef(0);

  // 🚨 UPDATED: Enhanced auth state change handler
  // 🆕 GAP-MOBILE-002 FIX: Track last login time to prevent reload on TOKEN_REFRESHED
  const lastLoginTimeRef = useRef(0);

  // 🚀 SCALABILITY FIX: Track current user via ref to avoid stale closure in onAuthStateChange
  // This ref is synced with user state and allows the auth listener to check current auth status
  const userRef = useRef(null);

  // 🚀 Keep userRef synced with user state (for use in callbacks with stale closures)
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("🔐 Auth event:", event, "| User exists:", !!userRef.current);

      // 🚀 SCALABILITY FIX: Skip re-processing if already authenticated
      // This prevents loading screens on page resume, saves API calls at scale
      // Only SIGNED_OUT should be processed when user exists
      if (userRef.current && event !== "SIGNED_OUT") {
        if (event === "TOKEN_REFRESHED" && session?.access_token) {
          // Silently update token without running full login flow
          setToken(session.access_token);
          console.log("🔄 Token updated silently (user already authenticated)");
        } else {
          console.log(`🔄 ${event} skipped - user already authenticated`);
        }
        return;
      }

      // Only process events when NOT authenticated
      if (event === "SIGNED_IN" && session?.user) {
        console.log("✅ User signed in via Supabase OAuth:", session.user.email);
        await handleSupabaseLogin(session);
      } else if (event === "SIGNED_OUT") {
        console.log("🔴 User signed out from Supabase");
        await handleSupabaseLogout();
      } else if (event === "INITIAL_SESSION" && session?.user) {
        console.log("🔄 Initial session found, auto-logging user");
        await handleSupabaseLogin(session);
      }
      // TOKEN_REFRESHED is handled in the early return block above when user exists
    });

    return () => subscription.unsubscribe();
  }, []);

  // 🚨 UPDATED: Check for existing session on app start
// 🆕 Native App Detection: Handle session tokens passed via URL hash from native app
// 🆕 GAP-MOBILE-002: Mobile page lifecycle handling to prevent reloads

useEffect(() => {
  const initAuth = async () => {
    // GAP-MOBILE-002: Track initialization count for debugging
    initCountRef.current += 1;
    const initId = initCountRef.current;
    console.log(`[AuthContext] initAuth #${initId} called at:`, new Date().toISOString());

    // GAP-MOBILE-002: Check if we should skip initialization (mobile restore)
    if (hasInitializedRef.current && !shouldInitialize('AuthContext')) {
      console.log(`[AuthContext] initAuth #${initId} SKIPPED - already initialized or mobile restore`);

      // Try to restore from persisted state
      const savedState = getPersistedState();
      if (savedState && savedState.user) {
        console.log('[AuthContext] Restoring user from persisted state:', savedState.user.email);
        setUser(savedState.user);
        
        // Restore token if present in persisted state
        if (savedState.token) {
          setToken(savedState.token);
          console.log('[AuthContext] Restored token from persisted state');
        }
        
        setLoading(false);
        return;
      }

      // If we have a user already, don't re-init
      if (user) {
        console.log('[AuthContext] User already set, skipping init');
        setLoading(false);
        return;
      }
    }

    hasInitializedRef.current = true;

    try {
      // 🆕 Check if we're receiving session from native app via URL hash
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const isNativeAuth = hashParams.get('type') === 'native_auth';

      // Detect native app via user agent or injected flag
      const isNativeApp = window.__IS_NATIVE_APP__ ||
        navigator.userAgent.includes('ScrollEd-Native');

      if (isNativeApp) {
        console.log("📱 Running inside native app");
      }

      // 🆕 Handle native app session injection via URL hash
      if (accessToken && (isNativeAuth || isNativeApp)) {
        console.log("📱 Native app session detected in URL hash, setting session...");

        try {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (sessionError) {
            console.error("❌ Failed to set native session:", sessionError);
            // 🆕 GAP-AUTH-REG-003-FIX: Token invalid, tell native to show native login
            if (isNativeApp && typeof window.__REQUEST_NATIVE_SIGN_OUT__ === 'function') {
              console.log("📱 Token invalid, returning to native login...");
              window.__REQUEST_NATIVE_SIGN_OUT__();
              return;
            }
          } else if (data?.session) {
            console.log("✅ Native session set successfully");
            // Clean URL hash after processing
            window.history.replaceState(null, '', window.location.pathname);
            await handleSupabaseLogin(data.session);
            return; // Exit early, session is set
          }
        } catch (nativeError) {
          console.error("❌ Error setting native session:", nativeError);
          // 🆕 GAP-AUTH-REG-003-FIX: Auth error, tell native to show native login
          if (isNativeApp && typeof window.__REQUEST_NATIVE_SIGN_OUT__ === 'function') {
            console.log("📱 Auth error, returning to native login...");
            window.__REQUEST_NATIVE_SIGN_OUT__();
            return;
          }
        }

        // Clean URL hash even if it failed
        window.history.replaceState(null, '', window.location.pathname);
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        console.log("🔄 Found existing Supabase session, auto-logging in");
        await handleSupabaseLogin(session);
      } else {
        // 🆕 GAP-AUTH-REG-003-FIX: No valid session in native app, show native login
        if (isNativeApp && typeof window.__REQUEST_NATIVE_SIGN_OUT__ === 'function') {
          console.log("📱 No valid session in native app, returning to native login...");
          window.__REQUEST_NATIVE_SIGN_OUT__();
          return;
        }

        // 🌍 ENHANCEMENT: Detect language on first app load (before login)
        console.log("[Language Detection] Starting smart language detection for non-authenticated user...");
        try {
          const detectionResult = await detectLanguage();
          console.log(`[Language Detection] Detected: ${detectionResult.language} (source: ${detectionResult.source})`);

          // Change language and store preference
          await i18n.changeLanguage(detectionResult.language);
          storeLanguagePreference(detectionResult.language);
          console.log(`🌍 App language set to: ${detectionResult.language}`);
        } catch (langError) {
          console.warn("[Language Detection] Failed to detect language, using fallback:", langError);
          // i18n already has fallback to English
        }

        // Fall back to regular token-based auth check
        await checkAuthStatus();
      }
    } catch (error) {
      console.error("❌ Error initializing auth:", error);
      await checkAuthStatus();
    } finally {
      setLoading(false);
    }
  };

  initAuth();
}, []);

  // 🆕 GAP-MOBILE-002: Persist auth state for mobile page lifecycle
  useEffect(() => {
    if (user && !loading) {
      // Persist state when user is set and not loading
      persistState({
        user: user,
        isAuthenticated: true,
        timestamp: Date.now(),
        token: getStoredToken(), // Save token for restore
      });
      console.log('[AuthContext] State persisted for mobile restore');
    }
  }, [user, loading]);

  // 🆕 GAP-MOBILE-002: Handle mobile page lifecycle events
  useEffect(() => {
    const handlePageShow = (event) => {
      console.log('[AuthContext] pageshow event - persisted:', event.persisted);
      if (event.persisted) {
        // Page restored from bfcache - state should be intact
        console.log('[AuthContext] Restored from bfcache - state preserved');
        // Force loading to false since state is preserved
        if (user) {
          setLoading(false);
        }
      }
    };

    const handlePageHide = (event) => {
      console.log('[AuthContext] pagehide event - persisted:', event.persisted);
      // Persist state before hiding
      if (user) {
        persistState({
          user: user,
          isAuthenticated: true,
          timestamp: Date.now(),
          token: getStoredToken(),
        });
      }
    };

    const handleFreeze = () => {
      console.log('[AuthContext] Page frozen (Page Lifecycle API)');
      if (user) {
        persistState({
          user: user,
          isAuthenticated: true,
          timestamp: Date.now(),
          token: getStoredToken(),
        });
      }
    };

    const handleResume = () => {
      console.log('[AuthContext] Page resumed (Page Lifecycle API)');
      // Don't re-initialize, just ensure loading is false
      if (user) {
        setLoading(false);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[AuthContext] Page hidden - persisting state');
        if (user) {
          persistState({
            user: user,
            isAuthenticated: true,
            timestamp: Date.now(),
            token: getStoredToken(),
          });
        }
      } else {
        console.log('[AuthContext] Page visible');
        // Check if we have persisted state but no user (page was discarded)
        if (!user && !loading) {
          const savedState = getPersistedState();
          if (savedState && savedState.user) {
            console.log('[AuthContext] Restoring user after visibility change:', savedState.user.email);
            setUser(savedState.user);
            if (savedState.token) {
              setToken(savedState.token);
            }
          }
        }
      }
    };

    // Add all listeners
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('freeze', handleFreeze);
    document.addEventListener('resume', handleResume);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('freeze', handleFreeze);
      document.removeEventListener('resume', handleResume);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, loading]);

  // 🚨 UPDATED: Enhanced Supabase login handler
  // 🆕 GAP-MOBILE-002 FIX: Accept options to control loading state
  const handleSupabaseLogin = async (session, options = {}) => {
    const { showLoading = true } = options;

    // 🆕 GAP-MOBILE-002: 5-second cooldown as SECONDARY protection
    // Prevents rapid re-logins from TOKEN_REFRESHED spam
    const now = Date.now();
    const timeSinceLastLogin = now - lastLoginTimeRef.current;
    if (timeSinceLastLogin < 5000 && lastLoginTimeRef.current > 0 && user) {
      console.log(`[AuthContext] handleSupabaseLogin SKIPPED - last login was ${timeSinceLastLogin}ms ago`);
      return;
    }
    lastLoginTimeRef.current = now;

    try {
      // 🆕 GAP-MOBILE-002: Only show loading for REAL logins, not token refreshes
      if (showLoading) {
        setLoading(true);
      }
      if (session.access_token) {
        setToken(session.access_token);
        console.log("✅ Supabase token stored");
      }

      const response = await api.get("/auth/me", {
        headers: getAuthHeader(),
      });
      // alert("response" + JSON.stringify(response.data));
      // alert("response" + response.data.user.onboarding_completed);

      // 🆕 Story 1.5.9: Read onboarding_completed from top-level user object (API returns it at top level)
      if (response.data.user.onboarding_completed) {
        localStorage.setItem("scrolled_onboarding_completed", "true");
      }
      const userData = {
        id: session.user.id,
        email: session.user.email,
        full_name:
          session.user.user_metadata?.full_name ||
          session.user.user_metadata?.name ||
          session.user.email?.split("@")[0],
        avatar_url:
          session.user.user_metadata?.avatar_url ||
          session.user.user_metadata?.picture,
        onboarding_completed: response.data.user.onboarding_completed || false, // 🆕 Read from top level, not preferences
        provider: session.user.app_metadata?.provider || "google",
      };


      // Store the Supabase token


      // Check if user exists in your backend, create if not
      await syncUserWithBackend(userData, session.access_token);

      // Set the user in state

      setUser(userData);
      setError(null);

      console.log("✅ User auto-logged in via Supabase:", userData.email);
    } catch (error) {
      console.error("❌ Error handling Supabase login:", error);
      setError("Failed to sign in automatically. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // 🚨 NEW: Sync Supabase user with your backend
  const syncUserWithBackend = async (userData, accessToken) => {
    try {
      // Use the /me endpoint instead of /auth/profile
      const response = await api.get("/auth/me", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data.user) {
        const user = response.data.user;

        // 🆕 Story 1.5.9: Set onboarding_completed from top-level user data (API returns it at top level, not in preferences)
        userData.onboarding_completed = user.onboarding_completed || false;

        // Merge user data
        Object.assign(userData, {
          username: user.username,
          full_name: user.full_name,
          avatar_url: user.avatar_url,
          bio: user.bio,
          created_at: user.created_at,
        });

        console.log(
          "✅ User data from /me endpoint, onboarding:",
          userData.onboarding_completed
        );

        // 🌍 ENHANCEMENT: Smart language detection with IP geolocation fallback
        // Priority: Profile → localStorage → browser language → IP geolocation → English
        if (user?.preferences?.language) {
          // User already has language preference in profile
          const savedLang = user.preferences.language;
          if (savedLang !== i18n.language) {
            console.log(`🌍 Loading user's language from profile: ${savedLang}`);
            await i18n.changeLanguage(savedLang);
          }
        } else {
          // No profile preference, use smart detection
          console.log("[Language Detection] No profile preference, starting smart detection...");
          try {
            const detectionResult = await detectLanguage();
            console.log(`[Language Detection] Detected: ${detectionResult.language} (source: ${detectionResult.source})`);

            if (detectionResult.language !== i18n.language) {
              await i18n.changeLanguage(detectionResult.language);
            }

            // Save detected language to profile for future logins
            storeLanguagePreference(detectionResult.language);
            try {
              await api.put("/user/preferences", { language: detectionResult.language });
              console.log(`🌍 Saved detected language to profile: ${detectionResult.language}`);
            } catch (e) {
              console.log("Could not persist language preference to profile:", e.message);
            }
          } catch (langError) {
            console.warn("[Language Detection] Smart detection failed, using existing language:", langError);
            // i18n will use whatever is currently set (browser language or fallback)
          }
        }

        // Set the user in state
        setUser(userData);
        setError(null);
      } else {
        console.warn("❌ No user data in /me response");
        // Fallback: set user with basic data
        setUser(userData);
      }
    } catch (error) {
      console.error("❌ Error getting user from /me endpoint:", error);
      // Still set user with basic data even if /me fails
      setUser(userData);
    }
  };

  // 🚨 UPDATED: Create user profile
  const createUserProfile = async (userData) => {
    try {
      const profileData = {
        id: userData.id,
        username:
          userData.email?.split("@")[0] || `user_${userData.id.slice(-8)}`,
        full_name: userData.full_name,
        avatar_url: userData.avatar_url,
        email: userData.email,
        created_at: new Date().toISOString(),
      };

      // Create profile via your backend API
      await api.put("/auth/profile", profileData, {
        headers: getAuthHeader(),
      });

      console.log("✅ User profile created for Supabase user");
    } catch (error) {
      console.error("❌ Error creating user profile:", error);
      // Don't throw here - we can still set the user in state
    }
  };

  // 🚨 NEW: Handle Supabase logout
  const handleSupabaseLogout = async () => {
    try {
      // Clear all local storage data
      clearToken();
      localStorage.removeItem("scrolled_onboarding_completed");
      localStorage.removeItem("show_onboarding_after_verification"); // Story 1.5.9
      localStorage.removeItem("scrolled_selected_instance");
      localStorage.removeItem("scrolled-instance-store");
      clearPersistedState(); // GAP-MOBILE-002: Clear mobile lifecycle state

      // GAP-MOBILE-002: Reset initialization guard
      hasInitializedRef.current = false;

      // Clear auth state
      setUser(null);
      setError(null);

      console.log("✅ Supabase logout handled");
    } catch (error) {
      console.error("❌ Error handling Supabase logout:", error);
    }
  };

  // 🚨 UPDATED: Enhanced Google OAuth login
  const loginWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);

      console.log("🔐 Starting Supabase Google OAuth...");

      // Use Supabase's built-in OAuth
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth-callback`,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });

      if (error) {
        console.error("❌ Supabase OAuth error:", error);
        throw error;
      }

      console.log(
        "✅ Google OAuth initiated - user will be redirected to Google"
      );

      return { success: true };
    } catch (error) {
      const errorMessage = error.message || "Google login failed";
      console.error("❌ Google login error:", errorMessage);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // 🚨 UPDATED: Enhanced logout to handle both Supabase and regular auth
  // 🆕 GAP-AUTH-REG-003: Added native app detection to return to native login screen
  const logout = async () => {
    try {
      setLoading(true);

      console.log("🔐 Logging out user...");

      // 🆕 Detect if we're running inside native app
      const isNativeApp = window.__IS_NATIVE_APP__ ||
        navigator.userAgent.includes('ScrollEd-Native');

      // Sign out from Supabase if user was using social login
      if (user?.provider) {
        const { error } = await supabase.auth.signOut();
        if (error) {
          console.error("❌ Supabase sign out error:", error);
        }
      }

      // Call backend logout endpoint for regular auth
      try {
        await api.post("/auth/signout");
      } catch (apiError) {
        console.warn("Backend logout call failed:", apiError);
      }

      // Clear all local storage data
      clearToken();
      localStorage.removeItem("scrolled_onboarding_completed");
      localStorage.removeItem("show_onboarding_after_verification"); // Story 1.5.9
      localStorage.removeItem("scrolled_selected_instance");
      localStorage.removeItem("scrolled-instance-store");
      clearPersistedState(); // GAP-MOBILE-002: Clear mobile lifecycle state

      // GAP-MOBILE-002: Reset initialization guard
      hasInitializedRef.current = false;

      // Clear auth state
      setUser(null);
      setError(null);

      console.log("✅ User logged out and all data cleared");

      // 🆕 GAP-AUTH-REG-003: If in native app, notify native to show native login screen
      if (isNativeApp && typeof window.__REQUEST_NATIVE_SIGN_OUT__ === 'function') {
        console.log("📱 Notifying native app of logout...");
        window.__REQUEST_NATIVE_SIGN_OUT__();
        // Don't redirect - let native app handle showing login screen
        return;
      }

      // Redirect back to the base path (web app only)
      const redirectPath = computeLogoutRedirect();
      window.location.assign(redirectPath);
    } catch (error) {
      console.error("❌ Logout error:", error);
      // Still clear local state even if backend call fails
      await handleSupabaseLogout();

      // 🆕 Check for native app even in error case
      const isNativeApp = window.__IS_NATIVE_APP__ ||
        navigator.userAgent.includes('ScrollEd-Native');

      if (isNativeApp && typeof window.__REQUEST_NATIVE_SIGN_OUT__ === 'function') {
        console.log("📱 Notifying native app of logout (after error)...");
        window.__REQUEST_NATIVE_SIGN_OUT__();
        return;
      }

      // Force redirect even if backend call fails (web app only)
      const redirectPath = computeLogoutRedirect();
      window.location.assign(redirectPath);
    } finally {
      setLoading(false);
    }
  };

  // Keep your existing functions (they should work as-is)
  const checkAuthStatus = async () => {
    try {
      const token = getStoredToken();
      if (!token) {
        setLoading(false);
        return;
      }

      console.log(
        "🔐 Checking auth status with token:",
        token.substring(0, 20) + "..."
      );

      const response = await api.get("/auth/me", {
        headers: getAuthHeader(),
      });

      console.log("✅ Auth /me response:", response.data);

      if (response.data.user) {
        const userData = response.data.user;

        // Decode JWT to get the sub claim
        try {
          const decodedToken = jwtDecode(token);
          userData.id = decodedToken.sub;
          console.log(
            "🔐 JWT decoded, using sub as user ID:",
            decodedToken.sub
          );
        } catch (decodeError) {
          console.error("❌ Failed to decode JWT token:", decodeError);
        }

        // 🆕 Story 1.5.9: Set user in state (was missing before!)
        // onboarding_completed is already at top level from API response
        setUser(userData);

        // Load user's language preference
        if (userData?.preferences?.language) {
          const savedLang = userData.preferences.language;
          if (savedLang !== i18n.language) {
            console.log(`🌍 Loading user's language on app startup: ${savedLang}`);
            await i18n.changeLanguage(savedLang);
          }
        } else {
          // Check localStorage as fallback
          const localStorageLang = localStorage.getItem('scrolled_preferred_language');
          if (localStorageLang && localStorageLang !== i18n.language) {
            console.log(`🌍 Loading language from localStorage on startup: ${localStorageLang}`);
            await i18n.changeLanguage(localStorageLang);
          }
        }

        console.log(
          "✅ User authenticated and set in context:",
          userData.email,
          "| onboarding_completed:",
          userData.onboarding_completed
        );
      } else {
        console.log("❌ No user data in response, removing token");
        clearToken();
      }
    } catch (error) {
      console.error("❌ Auth status check failed:", error);
      clearToken();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);

      const response = await api.post("/auth/signin", {
        email,
        password,
      });

      const { user, session } = response.data;

      if (session?.access_token) {
        setToken(session.access_token);

        // Decode JWT to get the sub claim
        try {
          const decodedToken = jwtDecode(session.access_token);
          user.id = decodedToken.sub; // Use sub claim as user ID
          console.log(
            "🔐 JWT decoded at login, using sub as user ID:",
            decodedToken.sub
          );
        } catch (decodeError) {
          console.error("❌ Failed to decode JWT token at login:", decodeError);
        }

        setUser(user);

        // Load user's language preference
        if (user?.preferences?.language) {
          const savedLang = user.preferences.language;
          if (savedLang !== i18n.language) {
            console.log(`🌍 Loading user's language after login: ${savedLang}`);
            await i18n.changeLanguage(savedLang);
          }
        } else {
          // Check localStorage as fallback
          const localStorageLang = localStorage.getItem('scrolled_preferred_language');
          if (localStorageLang && localStorageLang !== i18n.language) {
            console.log(`🌍 Loading language from localStorage: ${localStorageLang}`);
            await i18n.changeLanguage(localStorageLang);
          }
        }

        console.log("✅ User logged in:", user.email);
        console.log("🔄 About to set loading to false in finally block");
        return { success: true };
      }

      throw new Error("No session data received");
    } catch (error) {
      const rawMessage = error.response?.data?.error || "Login failed";
      const normalized = rawMessage.toLowerCase();
      const requiresVerification =
        normalized.includes("confirm") || normalized.includes("verify");
      const errorMessage = requiresVerification
        ? "Please verify your email before signing in. Check your inbox for our confirmation link."
        : rawMessage;
      setError(errorMessage);
      console.error("Login error:", errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      console.log("🔄 Setting loading to false in finally block");
      setLoading(false);
    }
  };

  const register = async (email, password) => {
    try {
      setError(null);
      setLoading(true);

      const response = await api.post("/auth/signup", {
        email,
        password,

      });

      const { user } = response.data;

      console.log("✅ User registered:", user.email);

      // After successful registration, automatically log in
      return await login(email, password);
    } catch (error) {
      const errorMessage = error.response?.data?.error || "Registration failed";
      setError(errorMessage);
      console.error("❌ Registration error:", errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // In your AuthContext.jsx - REPLACE the current loginWithGoogle function

  const updateProfile = async (profileData) => {
    try {
      setError(null);
      setLoading(true);

      const token = getStoredToken();
      if (!token) {
        throw new Error("No authentication token");
      }

      const response = await api.put("/auth/profile", profileData, {
        headers: getAuthHeader(),
      });

      const { user } = response.data;
      setUser(user);

      console.log("✅ Profile updated");
      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Profile update failed";
      setError(errorMessage);
      console.error("❌ Profile update error:", errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateUserProfile = (profileData) => {
    setUser((prev) => ({ ...prev, ...profileData }));
  };

  const clearError = () => {
    setError(null);
  };

  // 🔧 Centralized onboarding gating logic (Story 1.5.9)
  // Server is the source of truth; localStorage is just a cache
  const shouldShowOnboarding = React.useMemo(() => {
    // Use !!user instead of isAuthenticated (which doesn't exist yet)
    if (!user || loading) {
      return false; // Not applicable when not authenticated or still loading
    }

    // Check server-sourced flag from user object
    const serverOnboardingCompleted = user?.onboarding_completed || false;

    // Get cached localStorage value for comparison
    const localStorageCached = localStorage.getItem('scrolled_onboarding_completed') === 'true';

    // Sync localStorage cache with server truth
    if (serverOnboardingCompleted) {
      localStorage.setItem('scrolled_onboarding_completed', 'true');
    } else {
      localStorage.removeItem('scrolled_onboarding_completed');
    }

    // 🆕 Story 1.5.9: Telemetry breadcrumb for unexpected re-routing
    if (serverOnboardingCompleted === false && localStorageCached === true) {
      // User was marked complete locally but server says incomplete - unexpected state
      console.warn('⚠️ Onboarding state mismatch: localStorage=complete, server=incomplete');
      if (window.Sentry) {
        window.Sentry.addBreadcrumb({
          category: 'onboarding',
          message: 'Onboarding state mismatch detected',
          level: 'warning',
          data: {
            serverCompleted: serverOnboardingCompleted,
            localStorageCached: localStorageCached,
            userId: user?.id,
            userEmail: user?.email
          }
        });
      }
    }

    // Return opposite: show onboarding if NOT completed
    return !serverOnboardingCompleted;
  }, [user, loading, user?.onboarding_completed, user?.id, user?.email]);

  const value = {
    user,
    loading,
    error,
    login,
    register,
    loginWithGoogle,
    logout,
    updateProfile,
    updateUserProfile,
    clearError,
    isAuthenticated: !!user,
    getToken: getStoredToken,
    shouldShowOnboarding, // 🆕 Centralized gating flag
    isNativeApp: window.__IS_NATIVE_APP__ || navigator.userAgent.includes('ScrollEd-Native')
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
